import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '../primitives/Icon';

interface DrawerProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  subtitle?: string;
  /** Which side to open from; default is end (right in RTL, left in LTR) */
  side?: 'start' | 'end';
  /** Width in Tailwind size. Default 'md' (~28rem). */
  size?: 'sm' | 'md' | 'lg' | 'xl';
  footer?: React.ReactNode;
  children: React.ReactNode;
  closeOnBackdrop?: boolean;
  showCloseButton?: boolean;
}

const SIZE = {
  sm: 'w-full sm:w-[400px]',
  md: 'w-full sm:w-[500px]',
  lg: 'w-full sm:w-[640px]',
  xl: 'w-full sm:w-[820px]',
};

/**
 * Side-sheet / drawer. Slides in from the end edge (right in RTL).
 * Replaces centered modals for forms and detail views.
 */
export const Drawer: React.FC<DrawerProps> = ({
  open,
  onClose,
  title,
  subtitle,
  side = 'end',
  size = 'md',
  footer,
  children,
  closeOnBackdrop = true,
  showCloseButton = true,
}) => {
  useEffect(() => {
    if (!open) return;
    const handleEsc = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', handleEsc);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open) return null;

  // In RTL: start = right edge, end = left edge → we want drawer on RIGHT by default (start)
  // Since document is RTL, logical `start` = right. Default `end` = left, but UX-wise
  // users expect the drawer on the opposite side of the sidebar (which is on start).
  // So default end = left (opposite to sidebar which is start=right in RTL).
  // Per user request ("רבע עמוד שנפתח מהצד"), we open from the content edge.
  const sideClass = side === 'start'
    ? 'start-0 animate-[drawer-in-start_220ms_cubic-bezier(0.16,1,0.3,1)]'
    : 'end-0 animate-[drawer-in-end_220ms_cubic-bezier(0.16,1,0.3,1)]';

  return createPortal((
    <div
      className="fixed inset-0 z-[60] flex animate-fade-in"
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? 'drawer-title' : undefined}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
        onClick={closeOnBackdrop ? onClose : undefined}
      />

      {/* Drawer panel */}
      <div
        className={[
          'relative ms-auto h-full flex flex-col bg-white dark:bg-slate-900',
          'border-s border-slate-200 dark:border-slate-800',
          'shadow-2xl overflow-hidden',
          SIZE[size],
          sideClass,
          side === 'start' ? 'me-auto ms-0' : '',
        ].join(' ')}
      >
        {/* Header */}
        {(title || showCloseButton) && (
          <div className="px-6 py-5 border-b border-slate-100 dark:border-slate-800 flex items-start justify-between gap-3 flex-shrink-0">
            <div className="flex-1 min-w-0">
              {title && (
                <h2 id="drawer-title" className="text-lg font-bold text-slate-900 dark:text-white">
                  {title}
                </h2>
              )}
              {subtitle && <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{subtitle}</p>}
            </div>
            {showCloseButton && (
              <button
                onClick={onClose}
                className="size-9 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 flex items-center justify-center transition-colors flex-shrink-0"
                aria-label="סגור"
              >
                <Icon name="close" size={20} />
              </button>
            )}
          </div>
        )}

        {/* Body (scrollable) */}
        <div className="flex-1 overflow-y-auto px-6 py-5">{children}</div>

        {/* Footer */}
        {footer && (
          <div className="px-6 py-4 bg-slate-50 dark:bg-slate-900/50 border-t border-slate-100 dark:border-slate-800 flex items-center justify-end gap-3 flex-shrink-0">
            {footer}
          </div>
        )}

        <style>{`
          @keyframes drawer-in-end {
            from { transform: translateX(-100%); }
            to   { transform: translateX(0); }
          }
          @keyframes drawer-in-start {
            from { transform: translateX(100%); }
            to   { transform: translateX(0); }
          }
        `}</style>
      </div>
    </div>
  ), document.body);
};
